/**
 * <Banner> — a status banner: leading icon + message (atom, L0.1).
 *
 * A single row of an optional `<Icon>` + a `<Text>` message, tinted by a `color` token. Extensible
 * to any status (a success "Séance bien engagée", a danger/warning alert header, …) via `color` +
 * `icon`. `tinted` wraps it in a status-tinted pill whose background follows `color` (success →
 * `successLight`, …, falling back to `surface-subtle`); `align` places it centred (standalone banner)
 * or start-aligned (inside a card/row). Composes `<Row>` + `<Icon>` + `<Text>`. No hex.
 */
import React from "react"
import { View, type StyleProp, type ViewStyle } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { textInkOnFill, type SemanticColorKey } from "../../theme/tokens/colors"
import { Row } from "../containers/Row"
import { Icon, type IconName } from "../icons/Icon"
import { Text } from "../typography/Text"

export type BannerProps = {
    message: React.ReactNode
    /**
     * Icon + text color token — defaults to `text-primary`. Une couleur de statut s'écrit dans son
     * encre (`textInkOnFill` : `danger` → `text-danger`), pas dans son aplat.
     */
    color?: SemanticColorKey
    /** Leading FontAwesome5 glyph — omitted → text only. */
    icon?: IconName
    /** Render the icon as a filled glyph. */
    iconSolid?: boolean
    /** Horizontal placement — `center` (standalone) or `start` (inside a card/row). Defaults to `center`. */
    align?: "start" | "center"
    /** Wrap in a status-tinted pill (padding + radius); background follows `color` (see `TINT_BG`). */
    tinted?: boolean
    style?: StyleProp<ViewStyle>
    testID?: string
}

const ICON_SIZE = 18

/** Light background per status color — mirrors Badge's VARIANT_BG but points at the *Light tints. */
const TINT_BG: Partial<Record<SemanticColorKey, SemanticColorKey>> = {
    success: "successLight",
    warning: "warningLight",
    danger: "dangerLight",
    info: "infoLight",
    primary: "primary-soft",
}

export function Banner(props: BannerProps) {
    const {
        message,
        color = "text-primary",
        icon,
        iconSolid = false,
        align = "start",
        tinted = false,
        style,
        testID,
    } = props
    const ink = textInkOnFill(color)

    return (
        <Row
            testID={testID}
            gap="2"
            align="center"
            justify={align === "center" ? "center" : "start"}
            style={[tinted && styles.tinted(TINT_BG[color] ?? "surface-subtle"), style]}
        >
            {icon != null && (
                <View style={styles.icon}>
                    <Icon name={icon} color={ink} solid={iconSolid} size={ICON_SIZE} />
                </View>
            )}
            <Text weight="bold" color={ink} style={styles.message}>
                {message}
            </Text>
        </Row>
    )
}

const styles = StyleSheet.create((theme) => ({
    tinted: (bg: SemanticColorKey) => ({
        alignSelf: "stretch" as const,
        backgroundColor: theme.colors[bg],
        borderRadius: theme.radius.md,
        paddingVertical: theme.spacing["2"],
        paddingHorizontal: theme.spacing["3"],
    }),
    /**
     * L'icône ne cède jamais sa place : sur le web, le `<svg>` d'un glyphe est un enfant flex
     * rétrécissable, et un message de quatre lignes l'écrasait à 5,6 × 18 px. C'est le message
     * qui passe à la ligne.
     */
    icon: {
        flexShrink: 0,
        width: ICON_SIZE,
        height: ICON_SIZE,
    },
    message: {
        flexShrink: 1,
    },
}))
